import { BlogPost } from "@/data/blogPosts";

/**
 * Article-to-article internal linking.
 *
 * Extracted from contextualLinks.ts so the callout logic can be maintained
 * without touching the oversized parent module.
 *
 * Two mechanisms:
 *  1. Opportunistic in-body links — wraps a key phrase from a related post's
 *     title inside the body when that phrase happens to appear verbatim.
 *  2. "Keep reading" callout — the RELIABLE mechanism. Always surfaces up to 3
 *     related posts as clickable /blog/<slug> links at the end of the article,
 *     independent of whether in-body matching succeeded. This guarantees every
 *     post with related tags gets real article-to-article links.
 */

/** Checks if a position in HTML content is inside an existing anchor tag. */
function isInsideAnchor(html: string, pos: number): boolean {
  const before = html.substring(0, pos);
  const lastOpen = before.lastIndexOf("<a ");
  const lastClose = before.lastIndexOf("</a>");
  return lastOpen > lastClose;
}

/**
 * Extracts a key phrase (2-5 words) from a blog post title that is likely to
 * appear naturally in other posts' content.
 */
function extractKeyPhrase(title: string): string | null {
  const cleaned = title
    .replace(
      /^(How to|The|Why|When|What|Best|Standard|Building|Hire|Hiring|Scale|Streamlining|Structuring|Remote)\s+/i,
      "",
    )
    .trim();

  const words = cleaned.split(/\s+/).filter((w) => w.length > 2);
  if (words.length < 2) return null;

  const phrases = [
    words.slice(0, 4).join(" "),
    words.slice(0, 3).join(" "),
    words.slice(0, 2).join(" "),
  ];

  return phrases.find((p) => p.length > 5) || null;
}

/** Finds related posts by shared tags, returns max `count` posts. */
function getRelatedByTags(
  currentPost: BlogPost,
  allPosts: BlogPost[],
  count: number,
): BlogPost[] {
  const sameTag = allPosts
    .filter((p) => p.slug !== currentPost.slug)
    .map((p) => ({
      post: p,
      sharedTags: p.tags.filter((t) => currentPost.tags.includes(t)).length,
    }))
    .filter((x) => x.sharedTags > 0)
    .sort((a, b) => b.sharedTags - a.sharedTags);

  const byTags = sameTag.slice(0, count).map((x) => x.post);
  if (byTags.length > 0) return byTags;

  // Fallback: no shared tags → use same category (so genuinely tag-isolated
  // posts still get related article-to-article links). Prefer most recent.
  if (currentPost.category) {
    const sameCat = allPosts
      .filter(
        (p) =>
          p.slug !== currentPost.slug && p.category === currentPost.category,
      )
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, count);
    return sameCat;
  }

  return [];
}

/**
 * Injects article-to-article links into the content.
 * Returns the modified content plus the set of slugs that were linked.
 * Does NOT touch service or homepage links — those are handled by the caller.
 */
export function injectArticleLinks(
  content: string,
  currentPost: BlogPost,
  allPosts: BlogPost[],
): string {
  const hasCallout = content.includes("contextual-links-callout");

  const related = getRelatedByTags(currentPost, allPosts, 8);
  if (related.length === 0) return content;

  let modified = content;
  const linkedSlugs = new Set<string>();

  // --- Best-effort in-body contextual links ---
  for (const relatedPost of related) {
    if (linkedSlugs.size >= 3) break;

    const phrase = extractKeyPhrase(relatedPost.title);
    if (!phrase) continue;

    const lowerContent = modified.toLowerCase();
    const lowerPhrase = phrase.toLowerCase();
    let searchStart = 0;
    let found = false;

    while (!found) {
      const idx = lowerContent.indexOf(lowerPhrase, searchStart);
      if (idx === -1) break;

      if (!isInsideAnchor(modified, idx)) {
        const before = modified.substring(idx - 1, idx);
        if (
          before === ">" ||
          before === " " ||
          before === "." ||
          before === "," ||
          before === "(" ||
          before === '"' ||
          before === "\n"
        ) {
          const actualText = modified.substring(idx, idx + phrase.length);
          const replacement = `<a href="/blog/${relatedPost.slug}" class="text-primary hover:text-primary/80 underline decoration-primary/40 hover:decoration-primary transition-all" title="${relatedPost.title}">${actualText}</a>`;
          modified =
            modified.substring(0, idx) +
            replacement +
            modified.substring(idx + phrase.length);
          linkedSlugs.add(relatedPost.slug);
          found = true;
        }
      }
      searchStart = idx + phrase.length;
    }
  }

  // --- "Keep reading" callout (reliable article-to-article linking) ---
  // Always renders when related posts exist, independent of in-body matching.
  if (!hasCallout) {
    const calloutPosts = related
      .filter((p) => !linkedSlugs.has(p.slug))
      .slice(0, 3);

    if (calloutPosts.length >= 1) {
      const linkItems = calloutPosts
        .map(
          (l) =>
            `<a href="/blog/${l.slug}" class="text-primary hover:text-primary/80 underline decoration-primary/40 hover:decoration-primary transition-all">${l.title}</a>`,
        )
        .join(" &middot; ");

      modified += `
<div class="contextual-links-callout my-8 p-6 bg-muted/30 border border-border rounded-xl">
  <p class="text-muted-foreground text-sm mb-2 font-mono uppercase tracking-wider">Keep reading</p>
  <p class="text-base">${linkItems}</p>
</div>`;
    }
  }

  return modified;
}
